import React, { useEffect, useRef, useState } from "react";

import { BiEdit } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link as RouteLink } from "react-router-dom";
import { differenceInYears, format } from "date-fns";
import { Controller, useForm } from "react-hook-form";

import {
  Td,
  Th,
  Tr,
  Flex,
  Grid,
  Link,
  Text,
  Badge,
  Image,
  Modal,
  Stack,
  Table,
  Tbody,
  Thead,
  Button,
  Select,
  Divider,
  Textarea,
  FormLabel,
  ModalBody,
  AlertDialog,
  IconButton,
  FormControl,
  ModalFooter,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  AlertDialogBody,
  FormErrorMessage,
  ModalCloseButton,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
} from "@chakra-ui/react";

import { checkImage } from "helpers";
import { PatientTestItem } from "./PatientTestItem";
import { FilePreview, LocalFilePreview } from "./FilePreview";
import {
  useUpdatePatient,
  useDeleteAllFilesFromPatient,
} from "api/appointments";

export default function PatientDetails({
  patient,
  appointment,
}: {
  patient: any;
  appointment: any;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [imageSrc, setImageSrc] = useState<any | undefined>();
  const [image, setImage] = useState<any | null>();
  const uploadRef = useRef<any>(null);

  const imageModal = useDisclosure();
  const deleteAllReportsModal = useDisclosure();

  function openImagePreview(src: any) {
    if (checkImage(src.name)) {
      setImageSrc(src);
      imageModal.onOpen();
    }
  }
  const { handleSubmit, register, control, errors, watch, setValue } = useForm({
    defaultValues: {
      status: patient.status,
      remarks: patient.remarks,
    },
  });
  const watchStatus = watch("status");

  useEffect(() => {
    if (!image?.length) {
      setValue("document", undefined);
    }
  }, [image]);

  const [updatePatient, updatePatientInfo] = useUpdatePatient();

  function getPatientAge(patient: any) {
    const dob = new Date(patient.patientDOB);
    if (!dob) return "Not Specified";

    const now = new Date().getTime();
    let years = differenceInYears(now, dob);
    years = years < 0 ? 0 : years;
    return `${years} yrs`;
  }

  async function onSubmit(values: any) {
    if (image) {
      const docs: File = image.map((i: any) => i.img);
      values.document = docs;
    }
    await updatePatient({ ...values, patient, appointment });

    onClose();

    setImage(null);
  }

  return (
    <>
      <Modal isOpen={imageModal.isOpen} onClose={imageModal.onClose}>
        <ModalOverlay />
        <ModalContent
          onClick={imageModal.onClose}
          boxShadow="none"
          height="80vh"
          bg="rgba(0,0,0,0.4)"
        >
          <Flex justifyContent="center" alignItems="center" height="100%">
            {imageSrc?.url && (
              <Image
                src={imageSrc.url}
                maxH={"80vh"}
                width="auto"
                fallbackSrc={`https://via.placeholder.com/150/?text=${imageSrc.name
                  .split(".")[1]
                  .toUpperCase()}`}
              />
            )}
          </Flex>
        </ModalContent>
      </Modal>

      <Stack spacing="14">
        <Stack>
          <Flex justify="space-between">
            <Text fontSize="2xl" fontWeight="500" color="black.500">
              Details
            </Text>

            <Link as={RouteLink} to={`/appointments/${appointment.id}`}>
              <Text fontSize="sm" fontWeight="500" color="primary.500">
                {appointment.paymentStatus === "pending"
                  ? "Settle Payment"
                  : "View Appointment"}
              </Text>
            </Link>
          </Flex>
          <Table border="1px solid" borderColor="gray.200" borderRadius="md">
            <Tbody color="gray.500">
              <Tr>
                <Th w="35%">Reg No.</Th>
                <Td>{patient.id}</Td>
              </Tr>
              <Tr>
                <Th>Name</Th>
                <Td>{patient.patientName}</Td>
              </Tr>
              <Tr>
                <Th>Age</Th>
                <Td>{getPatientAge(patient)}</Td>
              </Tr>
              <Tr>
                <Th>Address</Th>
                <Td>{patient.patientAddress || "Not Specified"}</Td>
              </Tr>
              <Tr>
                <Th>Date and time of sample collection</Th>
                <Td>
                  {format(
                    appointment.scheduledCollectionTime,
                    "d MMM, yyyy h:mm aaa"
                  )}
                </Td>
              </Tr>
              <Tr>
                <Th>Status</Th>
                <Td>
                  <Badge
                    colorScheme={patient.status === "active" ? "blue" : "green"}
                  >
                    {patient.status}
                  </Badge>
                </Td>
              </Tr>
              <Tr>
                <Th>Remarks</Th>
                <Td>{patient.remarks || "---"}</Td>
              </Tr>
            </Tbody>
          </Table>
        </Stack>

        <Stack>
          <Text fontSize="2xl" fontWeight="500" color="black.500">
            Tests
          </Text>

          <Table border="1px solid" borderColor="gray.200" borderRadius="md">
            <Thead>
              <Tr bg="gray.100">
                <Th w="20%">Name</Th>

                <Th w="20%">Status</Th>

                <Th w="20%">Remarks</Th>

                <Th w="40%" textAlign="right">
                  {patient.files?.length ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Grid
                        gap={2}
                        templateColumns="repeat(auto-fit, 50px)"
                        justifyContent="flex-end"
                        autoFlow="dense"
                        flexGrow={1}
                        pt={2}
                        wrap="wrap"
                      >
                        {patient.files.map((file: any) => (
                          <FilePreview
                            key={file.url}
                            file={file}
                            appointment={appointment}
                            patient={patient}
                            openImagePreview={openImagePreview}
                            size={50}
                            showDelete={false}
                          />
                        ))}
                      </Grid>

                      <IconButton
                        p={0}
                        color="black.500"
                        variant="ghost"
                        onClick={onOpen}
                        disabled={patient.status === "completed"}
                        aria-label="edit report"
                        icon={<BiEdit />}
                      />

                      <IconButton
                        p={0}
                        color="red.300"
                        variant="ghost"
                        onClick={() => deleteAllReportsModal.onOpen()}
                        aria-label="delete photo"
                        icon={<RiDeleteBin6Line />}
                        disabled={patient.status === "completed"}
                      />
                    </Stack>
                  ) : (
                    <Button
                      leftIcon={<FaPlus />}
                      disabled={
                        appointment.paymentStatus === "pending" ||
                        appointment.status !== "sample_collected"
                      }
                      onClick={onOpen}
                    >
                      Upload in one
                    </Button>
                  )}
                </Th>
              </Tr>
            </Thead>

            <Tbody>
              {Object.values(patient.tests).map((test: any) => (
                <PatientTestItem
                  key={Date.now()}
                  test={test}
                  patient={patient}
                  appointment={appointment}
                  disabled={
                    appointment.paymentStatus === "pending" ||
                    appointment.status !== "sample_collected" ||
                    test.status === "completed"
                  }
                />
              ))}
            </Tbody>
          </Table>
        </Stack>
      </Stack>

      <DeletePatientFilesModal
        appointment={appointment}
        patient={patient}
        onClose={deleteAllReportsModal.onClose}
        isOpen={deleteAllReportsModal.isOpen}
      />
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalHeader bg="modalHeader.500" color="white">
              Upload Patient Report
            </ModalHeader>

            <ModalCloseButton color="faded.500" top="4" />

            <ModalBody>
              <Stack>
                <FormControl isInvalid={errors.status}>
                  <FormLabel htmlFor="status">Status</FormLabel>
                  <Controller
                    as={
                      <Select
                        disabled={
                          patient.status && patient.status === "completed"
                        }
                        placeholder="Select option"
                        ref={register}
                      >
                        <option value="active">Active</option>
                        <option value="rejected">Rejected</option>
                        <option value="completed">Completed</option>
                      </Select>
                    }
                    name="status"
                    control={control}
                    rules={{ required: true }}
                    defaultValue=""
                  />
                  <FormErrorMessage>Please Select a status</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="document">Documents</FormLabel>

                  <Stack direction="row">
                    {patient.files?.map((file: any) => (
                      <FilePreview
                        key={file.url}
                        file={file}
                        appointment={appointment}
                        patient={patient}
                        openImagePreview={openImagePreview}
                        size={100}
                        showDelete
                      />
                    ))}
                    {image &&
                      image.map((img: any, index: any) => (
                        <LocalFilePreview
                          key={index}
                          file={img}
                          image={image}
                          setImage={setImage}
                          index={index}
                          size={100}
                        />
                      ))}
                  </Stack>
                  {/* <Button
                    as="label"
                    colorScheme="gray"
                    variant="outline"
                    size="sm"
                    leftIcon={<FaPlus size="14"></FaPlus>}
                  >
                    Add Reports */}

                  <input
                    ref={(e) => {
                      uploadRef.current = e;
                      register(e, {
                        required:
                          watchStatus === "completed" &&
                          (!patient.files || patient.files.length === 0),
                      });
                    }}
                    onChange={(e) => {
                      let img = e.target?.files;
                      let imgs: any = [];
                      if (img) {
                        for (var i = 0; i < img?.length; i++) {
                          imgs.push({
                            img: img[i],
                            url: URL.createObjectURL(img[i]),
                          });
                        }
                      }
                      setImage(imgs);
                    }}
                    style={{ display: "none", marginTop: 10 }}
                    name="document"
                    placeholder="Select Document"
                    type="file"
                    accept="application/pdf"
                    multiple
                  />

                  <Button
                    mr={3}
                    onClick={() => uploadRef.current?.click()}
                    h="32px"
                    background="white"
                    marginTop="3px"
                    color="black"
                    border="1px solid black"
                  >
                    Upload
                  </Button>

                  {errors && Object.values(errors)?.length ? (
                    <div style={{ color: "red" }}>Please upload document.</div>
                  ) : null}
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="remarks">Remarks</FormLabel>

                  <Textarea
                    ref={register}
                    name="remarks"
                    placeholder="Enter Remarks"
                    type="text"
                  />
                </FormControl>
              </Stack>
            </ModalBody>

            <ModalFooter justifyContent="flex-start">
              <Button
                type="submit"
                mr={3}
                isLoading={updatePatientInfo.isLoading}
              >
                Update
              </Button>

              <Button variant="outline" colorScheme="gray" onClick={onClose}>
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
}

function DeletePatientFilesModal({
  onClose,
  appointment,
  patient,
  isOpen,
}: any) {
  const [deleteAllImagesFromPatient, deleteAllImagesFromPatientInfo] =
    useDeleteAllFilesFromPatient();

  const cancelRef = React.useRef<any>();
  async function onSubmit() {
    await deleteAllImagesFromPatient({
      appointment,
      patient,
    });
    onClose();
  }
  return (
    <AlertDialog
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isOpen={isOpen}
    >
      <AlertDialogOverlay />

      <AlertDialogContent maxW="50vw">
        <Flex direction="column">
          <AlertDialogHeader bg="white" color="black" borderTopRadius="4px">
            Delete reports?
          </AlertDialogHeader>

          <AlertDialogCloseButton color="richText.500" fontSize="14" />

          <Divider color="border.500" height="2px"></Divider>

          <AlertDialogBody marginY={4}>
            <Text size="sm" color="settingText.500">
              Are you sure you want to delete all reports from this patient?
            </Text>
          </AlertDialogBody>

          <Divider />

          <AlertDialogFooter>
            <Button
              bg="red.300"
              onClick={onSubmit}
              isLoading={deleteAllImagesFromPatientInfo.isLoading}
            >
              Delete
            </Button>

            <Button
              ml={3}
              variant="outline"
              border="2px"
              borderColor="border.500"
              ref={cancelRef}
              onClick={onClose}
            >
              Cancel
            </Button>
          </AlertDialogFooter>
        </Flex>
      </AlertDialogContent>
    </AlertDialog>
  );
}
